import axios from 'axios'
import {API_URL} from '../../../../_metronic/helpers'

// The goods/services ID catalog as the public search page and the ID picker of the product form
// read it: the search (GET api/stuffid/query) and the category tree (GET api/stuffid/tag/), whose
// groups and subgroups carry the number of IDs in each. The server leaves empty categories out
// and the browser keeps the tree for five minutes (Cache-Control); this module keeps it for the
// page too, so a picker opened again shows its categories at once.

export const STUFFID_SEARCH_URL = `${API_URL}/stuffid/query`
const GROUPS_URL = `${API_URL}/stuffid/tag/`
const GROUPS_LIFETIME_MS = 5 * 60 * 1000

export type StuffIdSubgroup = {
  id: number
  name: string
  count?: number | null
  /**
   * The count split as the search filters split the catalog, eight numbers: general goods taxable,
   * exempt; general services taxable, exempt; then the same for dedicated IDs (backend 1.0.0.524).
   */
  breakdown?: number[] | null
}

export type StuffIdGroup = {
  id: number
  name: string
  count?: number | null
  breakdown?: number[] | null
  tags: StuffIdSubgroup[]
}

/** The search page's filters: '' both, kind '1' goods / '2' services, scope '1' general / '2' dedicated, vat '1' taxable / '2' exempt. */
export type StuffIdFilters = {
  kind: '' | '1' | '2'
  scope: '' | '1' | '2'
  vat: '' | '1' | '2'
}

const BREAKDOWN_SIZE = 8

/** How many IDs of a group or subgroup the filters leave; without a breakdown (an older server), all of them. */
export const countForFilters = (
  item: {count?: number | null; breakdown?: number[] | null},
  filters: StuffIdFilters
) => {
  const breakdown = item.breakdown
  if (!Array.isArray(breakdown) || breakdown.length !== BREAKDOWN_SIZE) {
    return Number(item.count || 0)
  }

  return breakdown.reduce((sum, value, index) => {
    const general = index < 4
    const service = (index & 2) !== 0
    const taxable = (index & 1) === 0
    const matches =
      (filters.scope === '' || (filters.scope === '1') === general) &&
      (filters.kind === '' || (filters.kind === '2') === service) &&
      (filters.vat === '' || (filters.vat === '1') === taxable)
    return matches ? sum + Number(value || 0) : sum
  }, 0)
}

// Each group's icon (Line Awesome), found by a word of its name; a group the administrator adds
// later and no word fits gets the folder.
const GROUP_ICONS: Array<[string[], string]> = [
  [['پیمانکاری'], 'la-hard-hat'],
  [['تعمیر'], 'la-tools'],
  [['خدمات فناوری'], 'la-server'],
  [['خدمات صنعتی'], 'la-industry'],
  [['خدمات شهری', 'محیط زیست'], 'la-recycle'],
  [['خدمات تخصصی', 'مشاوره'], 'la-user-tie'],
  [['خودرو'], 'la-car'],
  [['وسایل نقلیه'], 'la-bus'],
  [['حمل'], 'la-truck'],
  [['ساختمان', 'مصالح'], 'la-building'],
  [['پوشاک', 'نساجی'], 'la-tshirt'],
  [['فلزات', 'فلزی'], 'la-cubes'],
  [['برق', 'الکترونیک'], 'la-bolt'],
  [['ماشینآلات', 'صنعتی'], 'la-cogs'],
  [['سوخت', 'انرژی'], 'la-gas-pump'],
  [['کشاورزی', 'دامپروری'], 'la-tractor'],
  [['لوله', 'شیرآلات'], 'la-tint'],
  [['پلیمر', 'پلاستیک', 'بستهبندی'], 'la-box'],
  [['فناوری', 'ارتباطات'], 'la-laptop'],
  [['غذایی', 'خوراکی'], 'la-utensils'],
  [['دارو', 'سلامت', 'پزشکی'], 'la-briefcase-medical'],
  [['لوازم خانگی'], 'la-blender'],
  [['شیمیایی'], 'la-flask'],
  [['آرایشی', 'بهداشتی'], 'la-spray-can'],
  [['چوب', 'کاغذ', 'چاپ'], 'la-tree'],
  [['مبلمان', 'دکوراسیون'], 'la-couch'],
  [['طلا', 'جواهر', 'ساعت'], 'la-gem'],
  [['اداری'], 'la-paperclip'],
  [['ورزش', 'سرگرمی'], 'la-futbol'],
  [['معدن'], 'la-mountain'],
  [['دخانیات'], 'la-smoking'],
  [['مالی', 'بانکی', 'بیمه'], 'la-coins'],
  [['آزمایشگاه', 'بازرسی', 'استاندارد'], 'la-microscope'],
  [['آموزش', 'پژوهش'], 'la-graduation-cap'],
  [['تبلیغات', 'رسانه'], 'la-bullhorn'],
  [['نیروی انسانی'], 'la-users'],
  [['گردشگری', 'اقامت', 'پذیرایی'], 'la-concierge-bell'],
  [['بازرگانی', 'اجاره', 'املاک'], 'la-handshake'],
]

const iconKey = (value: string) =>
  normalizeStuffIdSearch(String(value || ''))
    .replace(/‌/g, '')
    .replace(/\s+/g, ' ')
    .trim()

/** The Line Awesome icon of a group, e.g. «قطعات و لوازم خودرو» → la-car. */
export const groupIcon = (name?: string | null) => {
  const key = iconKey(name || '')
  const match = GROUP_ICONS.find(([words]) => words.some((word) => key.includes(word)))
  return match ? match[1] : 'la-folder'
}

/** A category of a search result («tg»): a subgroup with its group («pa», «gn»), or a group. */
export type StuffIdCategoryLabel = {
  id: number
  pa?: number | null
  nm: string
  gn?: string | null
}

/** What a category filter is set to: a whole group, one subgroup, or nothing. */
export type StuffIdCategoryChoice = {
  groupId?: number
  tagId?: number
}

let cachedGroups: {at: number; promise: Promise<StuffIdGroup[]>} | undefined

const toGroups = (data: unknown): StuffIdGroup[] =>
  Array.isArray(data)
    ? data
        .filter((group) => group && Number(group.id) > 0)
        .map((group) => ({
          id: Number(group.id),
          name: String(group.name || ''),
          count: group.count ?? null,
          breakdown: Array.isArray(group.breakdown) ? group.breakdown.map(Number) : null,
          tags: (Array.isArray(group.tags) ? group.tags : [])
            .filter((tag: StuffIdSubgroup) => tag && Number(tag.id) > 0)
            .map((tag: StuffIdSubgroup) => ({
              id: Number(tag.id),
              name: String(tag.name || ''),
              count: tag.count ?? null,
              breakdown: Array.isArray(tag.breakdown) ? tag.breakdown.map(Number) : null,
            })),
        }))
    : []

/** The tree, asked once in five minutes; a failed request is not kept, so the next use asks again. */
export const loadStuffIdGroups = (): Promise<StuffIdGroup[]> => {
  if (cachedGroups && Date.now() - cachedGroups.at < GROUPS_LIFETIME_MS) {
    return cachedGroups.promise
  }

  const promise = axios.get(GROUPS_URL).then((response) => toGroups(response.data))
  const entry = {at: Date.now(), promise}
  cachedGroups = entry
  promise.catch(() => {
    if (cachedGroups === entry) {
      cachedGroups = undefined
    }
  })
  return promise
}

/** For tests: the next load asks the server again. */
export const forgetStuffIdGroups = () => {
  cachedGroups = undefined
}

export const formatStuffIdNumber = (value?: number | null) =>
  Number(value || 0).toLocaleString('fa-IR')

/** The server writes totals with group separators («4,123,456»). */
export const parseStuffIdTotal = (value: unknown) => {
  const parsed = Number(normalizeStuffIdDigits(String(value ?? '')).replace(/[^\d]/g, '') || '0')
  return Number.isFinite(parsed) ? parsed : 0
}

export const normalizeStuffIdDigits = (value: string) =>
  value.replace(/[۰-۹٠-٩]/g, (digit) => {
    const code = digit.charCodeAt(0)
    return String(code >= 0x06f0 ? code - 0x06f0 : code - 0x0660)
  })

/** Persian or Arabic digits and letters typed or pasted search the same as their usual form. */
export const normalizeStuffIdSearch = (value: string) =>
  normalizeStuffIdDigits(value).replace(/[يى]/g, 'ی').replace(/ك/g, 'ک')

/** A term searches from two letters, or three digits of a 13-digit code. */
export const isSearchableStuffIdTerm = (value: string) => {
  const term = value.trim()
  return /^\d+$/.test(term) ? term.length >= 3 : term.length >= 2
}

export const stuffIdSearchErrorMessage = (requestError: any) => {
  if (
    requestError?.code === 'ECONNABORTED' ||
    requestError?.response?.status === 408 ||
    requestError?.response?.status === 504
  ) {
    return 'پاسخ جستجو بیش از حد معمول طول کشید. لطفاً دوباره تلاش کنید یا عبارت دقیق‌تری بنویسید.'
  }

  const serverMessage =
    requestError?.response?.data?.Message || requestError?.response?.data?.message
  if (serverMessage && !/an error has occurred/i.test(serverMessage)) {
    return serverMessage
  }

  return 'جستجوی شناسه کالا/خدمت انجام نشد. لطفاً دوباره تلاش کنید.'
}

export const categoryLabelPath = (label: StuffIdCategoryLabel) =>
  label.gn && label.gn !== label.nm ? `${label.gn} / ${label.nm}` : label.nm

/** The filter a result's category badge sets: its subgroup, or its group. */
export const choiceOfLabel = (label: StuffIdCategoryLabel): StuffIdCategoryChoice =>
  label.pa ? {groupId: label.pa, tagId: label.id} : {groupId: label.id}

/** The query parameters of api/stuffid/query for a choice. */
export const categoryChoiceParams = (choice: StuffIdCategoryChoice) =>
  choice.tagId ? {filter_t: choice.tagId} : choice.groupId ? {filter_tg: choice.groupId} : {}

/** The group and subgroup of a choice in the tree; a subgroup finds its own group. */
export const findCategoryChoice = (groups: StuffIdGroup[], choice: StuffIdCategoryChoice) => {
  const group = choice.tagId
    ? groups.find((item) => item.tags.some((tag) => tag.id === choice.tagId))
    : groups.find((item) => item.id === choice.groupId)
  const tag = choice.tagId ? group?.tags.find((item) => item.id === choice.tagId) : undefined
  return {group, tag}
}

/** «group» or «group / subgroup»; empty while the tree is not loaded. */
export const describeCategoryChoice = (groups: StuffIdGroup[], choice: StuffIdCategoryChoice) => {
  const {group, tag} = findCategoryChoice(groups, choice)
  return group ? (tag ? `${group.name} / ${tag.name}` : group.name) : ''
}

export const sameCategoryChoice = (first: StuffIdCategoryChoice, second: StuffIdCategoryChoice) =>
  (first.tagId || 0) === (second.tagId || 0) &&
  (first.tagId ? true : (first.groupId || 0) === (second.groupId || 0))
